import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import {
  DashboardSearchParams,
  dashboardSearchParamsSchema,
  selectedVirtualDimensionsValuesSearchParamsSchema,
} from "./searchParamsSchema"

export interface UseDashboardSearchParamsReturn {
  // Date range
  dateRange: [string | null, string | null]
  datePreset: string | null
  setDateRange: (dateRange: [string | null, string | null]) => void
  setDatePreset: (preset: string | null) => void
  setDatePresetWithDates: (
    preset: string | null,
    from: string,
    to: string,
  ) => void

  // Virtual dimensions
  selectedVirtualDimensionsValues: Array<{
    bqName: string
    values: string[]
  }>
  setSelectedVirtualDimensionsValues: (
    values: Array<{
      bqName: string
      values: string[]
    }>,
  ) => void

  // Utility methods
  clearDateRange: () => void
  clearAllParams: () => void
  getValidatedParams: () => DashboardSearchParams
}

export const useDashboardSearchParams = (): UseDashboardSearchParamsReturn => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Parse and validate search params
  const validatedParams = useMemo(() => {
    const rawParams = Object.fromEntries(searchParams.entries())

    try {
      return dashboardSearchParamsSchema.parse(rawParams)
    } catch (error) {
      console.warn("Invalid dashboard search params:", error)
      return {} as DashboardSearchParams
    }
  }, [searchParams])

  // Date range getters
  const dateRange: [string | null, string | null] = useMemo(
    () => [validatedParams.from || null, validatedParams.to || null],
    [validatedParams.from, validatedParams.to],
  )

  const datePreset = useMemo(
    () => validatedParams.datePreset || null,
    [validatedParams.datePreset],
  )

  // Virtual dimensions getter
  const selectedVirtualDimensionsValues = useMemo(() => {
    const param = validatedParams.selectedVirtualDimensionsValues

    if (!param) return []

    try {
      return selectedVirtualDimensionsValuesSearchParamsSchema.parse(
        JSON.parse(param),
      )
    } catch (error) {
      console.warn("Invalid virtual dimensions values:", error)
      return []
    }
  }, [validatedParams.selectedVirtualDimensionsValues])

  // Date range setters
  const setDateRange = useCallback(
    (dateRange: [string | null, string | null]) => {
      const [from, to] = dateRange
      const newParams = new URLSearchParams(searchParams)

      if (from && to) {
        newParams.set("from", from)
        newParams.set("to", to)
      } else if (from && !to) {
        newParams.set("from", from)
        newParams.delete("to")
      } else if (!from && to) {
        newParams.delete("from")
        newParams.set("to", to)
      } else {
        newParams.delete("from")
        newParams.delete("to")
      }

      setSearchParams(newParams)
    },
    [searchParams, setSearchParams],
  )

  const setDatePreset = useCallback(
    (preset: string | null) => {
      const newParams = new URLSearchParams(searchParams)

      if (preset) {
        newParams.set("datePreset", preset)
      } else {
        newParams.delete("datePreset")
      }

      setSearchParams(newParams)
    },
    [searchParams, setSearchParams],
  )

  // Combined function to set both preset and dates
  const setDatePresetWithDates = useCallback(
    (preset: string | null, from: string, to: string) => {
      const newParams = new URLSearchParams(searchParams)

      if (preset) {
        newParams.set("datePreset", preset)
        newParams.set("from", from)
        newParams.set("to", to)
      } else {
        newParams.delete("datePreset")
        newParams.delete("from")
        newParams.delete("to")
      }

      setSearchParams(newParams)
    },
    [searchParams, setSearchParams],
  )

  // Virtual dimensions setter
  const setSelectedVirtualDimensionsValues = useCallback(
    (
      values: Array<{
        bqName: string
        values: string[]
      }>,
    ) => {
      const newParams = new URLSearchParams(searchParams)

      if (values.length > 0) {
        newParams.set("selectedVirtualDimensionsValues", JSON.stringify(values))
      } else {
        newParams.delete("selectedVirtualDimensionsValues")
      }

      setSearchParams(newParams)
    },
    [searchParams, setSearchParams],
  )

  // Utility methods
  const clearDateRange = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete("from")
    newParams.delete("to")
    newParams.delete("datePreset")
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const clearAllParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete("from")
    newParams.delete("to")
    newParams.delete("datePreset")
    newParams.delete("selectedVirtualDimensionsValues")
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const getValidatedParams = useCallback(
    () => validatedParams,
    [validatedParams],
  )

  return {
    // Date range
    dateRange,
    datePreset,
    setDateRange,
    setDatePreset,
    setDatePresetWithDates,

    // Virtual dimensions
    selectedVirtualDimensionsValues,
    setSelectedVirtualDimensionsValues,

    // Utility methods
    clearDateRange,
    clearAllParams,
    getValidatedParams,
  }
}
